// Active dataset behind Testing mode.
//
// Defaults to the static fixture in packages/demo-data. When the signed-in
// brokerage picked its agency at signup, main.tsx swaps in the imported set
// (their real listings + team) BEFORE testFixtures.ts is evaluated, so the
// fixtures build from the right source without any per-page changes.
//
// Only four values vary per enterprise — everything else (lead counts, page
// views, clicks, forms, campaigns, billing) has no equivalent in scraped data
// and stays synthetic. See DemoDataResponse below.
import * as STATIC from '@proptr/demo-data';
import type { DemoDataResponse } from '@proptr/shared';
import { setDemoEnterpriseId } from './testMode.js';

export {
  AGENT_BIO_CHANNELS,
  AGENT_BUTTON_CLICKS,
  AGENT_LEADS,
  AGENT_LISTINGS,
  AGENT_LISTING_CLICKS,
  AGENT_LISTING_INQ,
  AGENT_PAGE_VIEWS,
  AGENT_PERMITS,
  AGENT_RECOVERY_CODES,
  CAMPAIGNS,
  DEMO_FORM_DEFS,
  SYSTEM_LOGS,
  LISTING_LEADS,
  PROPTREE_SHORT_IDS,
  demoFormQuestions,
  FACE,
  IMG,
} from '@proptr/demo-data';
export type { DemoFormDef } from '@proptr/demo-data';

// `let` so the swap below is visible to importers as a live binding.
export let AGENCY = STATIC.AGENCY;
export let AGENTS = STATIC.AGENTS;
export let LISTING_SEED = STATIC.LISTING_SEED;
export let LISTING_IMAGES = STATIC.LISTING_IMAGES;

/** True once a per-enterprise set has replaced the static fixture. */
export let isAgencyDataset = false;

export type { DemoDataResponse } from '@proptr/shared';

/**
 * Swap in the enterprise's imported dataset. Must run before testFixtures.ts
 * is evaluated — main.tsx enforces that with a dynamic import.
 *
 * Anything missing or empty leaves the static value in place, so a partial
 * import degrades to the built-in demo rather than an empty product.
 */
export function setDemoSource(data: DemoDataResponse): void {
  if (data.source !== 'agency') return;

  if (data.agency?.company) {
    AGENCY = {
      ...STATIC.AGENCY,
      company: data.agency.company,
      tagline: data.agency.tagline || STATIC.AGENCY.tagline,
      image: data.agency.image || STATIC.AGENCY.image,
      // Source-side slug, so the manager's @handle matches the one the
      // public preview renders.
      alias: data.agency.alias || STATIC.AGENCY.alias,
    };
  }

  if (data.agents?.length) {
    AGENTS = data.agents.map((a, i) => ({
      id: a.id || `pf-agent-${i}`,
      name: a.name,
      email: a.email ?? '',
      phone: a.phone ?? '',
      alias: a.alias,
      // Some agents have no portrait on the source site — fall back to the
      // static headshot for that slot so avatars never render broken.
      image: a.image || STATIC.AGENTS[i % STATIC.AGENTS.length]!.image,
    }));
  }

  if (data.listings?.length) {
    LISTING_SEED = data.listings.map((l) => ({
      title: l.title,
      price: l.price,
      bed: l.bed,
      bath: l.bath,
      area: l.area,
      type: l.type,
      sale: l.sale,
    }));
    // Index-aligned with LISTING_SEED, which is how the fixtures pair a
    // listing with its photo.
    LISTING_IMAGES = data.listings.map(
      (l, i) => l.image || STATIC.LISTING_IMAGES[i % STATIC.LISTING_IMAGES.length]!,
    );
  }

  isAgencyDataset = true;
}

/**
 * Fetch the enterprise's dataset with the UNPATCHED fetch. Called from
 * main.tsx before installTestModeFetch(), otherwise the interceptor would
 * intercept this very request.
 */
export async function loadDemoSource(): Promise<void> {
  try {
    const base = import.meta.env.VITE_API_URL ?? '';
    const res = await window.fetch(`${base}/api/me/demo-data`, {
      credentials: 'include',
    });
    if (!res.ok) return; // not signed in yet, or no session — stay static
    const data = (await res.json()) as DemoDataResponse;
    setDemoSource(data);
    // Preview iframes are rendered by the landing app, which has no
    // session — it needs the id in the URL to resolve the same dataset.
    // Only set when the import actually landed, so a static-fixture
    // session never sends a lookup that can only answer "static".
    if (data.source === 'agency' && data.enterprise) {
      setDemoEnterpriseId(data.enterprise);
    }
  } catch {
    // Offline or API down. Static fixture is a perfectly good fallback.
  }
}
